// Box chains — the sequenced upstream-Box topology forest
// (radius_box_chain). The detector in internal/radiusissues uses this
// tree to suppress r_box_wrong_box for users whose switch is anywhere
// in the head Box's subtree. Two surfaces here:
//   • `list` — the flat forest (subscriber detail page just looks up
//     which chain a switch belongs to).
//   • `state` + the CRUD ops (`add` / `remove` / `reorder` / `reparent`)
//     — the full /box_chains.php editor.
// Go side: internal/boxchains.

import type { ApiClient } from "./client.js";

/** One node in a chain's flat node map (keyed by normalised switch name). */
export interface BoxChainNode {
  norm: string;
  label: string;
  /** The parent node's `norm`, or null for the chain head. */
  parent: string | null;
  position: number;
  confirmed_at: string;
}
/** One box tree, rooted at a head node. `nodes` is keyed by `norm`. */
export interface BoxChain {
  head_norm: string;
  head_label: string;
  nodes: Record<string, BoxChainNode>;
}
export interface BoxChainListResponse {
  ok: boolean;
  chains: BoxChain[];
  error?: string;
}

export interface BoxChainAddResult {
  ok: boolean;
  error?: string;
}

// ── /v1/api/boxchains/state — the editor payload ──────────────────────

/** One node, augmented with live + shielded data for the editor tree. */
export interface BoxChainStateNode {
  norm: string;
  label: string;
  /** The parent node's `norm`, or null for the head. */
  parent: string | null;
  position: number;
  confirmed_at: string;
  /** A PPPoE user is currently online behind this Box. */
  online: boolean;
  /** Number of r_box_wrong_box issues this edge suppresses (0 for the head). */
  shielded: number;
}
/** One Box tree — flat node list (rebuild the hierarchy from each node's `parent`). */
export interface BoxChainStateChain {
  head_norm: string;
  head_label: string;
  /** null = no ONU snapshot for the head Box; true/false = on/offline. */
  head_online: boolean | null;
  /** Descendant count (excludes the head). */
  num_desc: number;
  /** Sum of `shielded` across the tree. */
  tree_shield: number;
  /** Includes the head (parent = null). */
  nodes: BoxChainStateNode[];
}
export interface BoxChainTotals {
  chains: number;
  /** Total descendants across all trees (excludes heads). */
  nodes: number;
  shielded: number;
}
export interface BoxChainStateResponse {
  ok: boolean;
  chains?: BoxChainStateChain[];
  totals?: BoxChainTotals;
  /** Every Box-switch name — feeds the picker modals (a switch can only be a child once). */
  all_boxes?: string[];
  error?: string;
}

/** {ok, deleted?, error?} — the reply shape of remove / reorder / reparent. */
export interface BoxChainOpResult {
  ok: boolean;
  deleted?: number;
  error?: string;
}

export function makeBoxChainsApi(client: ApiClient) {
  return {
    /** Every box chain (a forest of trees). */
    list(): Promise<BoxChainListResponse> {
      return client.get<BoxChainListResponse>("/v1/api/boxchains/list");
    },
    /** The editor payload — chains (with live + shielded), KPI totals, and
     *  the Box-switch list for the picker modals. */
    state(): Promise<BoxChainStateResponse> {
      return client.get<BoxChainStateResponse>("/v1/api/boxchains/state");
    },
    /** Add a chain link — `child_label`'s switch hangs off `parent_label`'s
     *  at `position` (1-based; defaults to 999 ⇒ append after all siblings).
     *  Creating the first link of a new chain also implicitly creates the
     *  head. Both must be Box switches; the child can't already have a parent. */
    add(args: { parent_label: string; child_label: string; position?: number }): Promise<BoxChainAddResult> {
      return client.post<BoxChainAddResult>("/v1/api/boxchains/add", {
        parent_label: args.parent_label,
        child_label: args.child_label,
        position: args.position ?? 999,
      });
    },
    /** Remove a node and its whole subtree (`{ child_norm }`), or drop an
     *  entire tree (`{ head_norm, all: true }`). */
    remove(args: { child_norm: string } | { head_norm: string; all: true }): Promise<BoxChainOpResult> {
      return client.post<BoxChainOpResult>("/v1/api/boxchains/remove", args);
    },
    /** Re-sequence the direct children of `parent_norm` to match `child_order`. */
    reorder(args: { parent_norm: string; child_order: string[] }): Promise<BoxChainOpResult> {
      return client.post<BoxChainOpResult>("/v1/api/boxchains/reorder", args);
    },
    /** Move `child_norm` (and its subtree) under `new_parent_label` at `position`. */
    reparent(args: { child_norm: string; new_parent_label: string; position: number }): Promise<BoxChainOpResult> {
      return client.post<BoxChainOpResult>("/v1/api/boxchains/reparent", args);
    },
  };
}

export type BoxChainsApi = ReturnType<typeof makeBoxChainsApi>;
